import { useEffect, useState } from 'react'
import { Command } from 'cmdk'
import { Search, User, X, Package, Receipt } from 'lucide-react'
import { useProductSearch } from '@/api/products'
import { useCustomerSearch } from '@/api/customers'
import { useSearchOrders } from '@/api/receipts'
import { usePOSStore } from '@/stores/posStore'
import { useUIStore } from '@/stores/uiStore'
import { useDebounce, useSelectCustomer } from '@/hooks'
import { formatPrice, formatCurrency } from '@/lib/utils'
import { ProductIcon, getCategoryIcon } from '@/lib/icons'
import { withCdnImageWidth } from '@/lib/cdnImage'
import { OrderDetailModal } from '@/components/orders/OrderDetailModal'
import { BlockedBadge } from '@/components/customers/BlockedBadge'
import type { Product, Customer } from '@/types'

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type SearchFilter = 'all' | 'products' | 'customers' | 'orders'

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<SearchFilter>('all')
  const debouncedSearch = useDebounce(search, 300)
  const { setActiveTab } = usePOSStore()
  const requestProductModal = useUIStore((s) => s.requestProductModal)
  const selectCustomer = useSelectCustomer()
  const [openOrder, setOpenOrder] = useState<{ receiptId: number; number: string } | null>(null)

  // Search queries are gated on the active filter so toggling to a single
  // type skips the other endpoints entirely.
  const baseEnabled = open && debouncedSearch.length >= 2
  const productsEnabled = baseEnabled && (filter === 'all' || filter === 'products')
  const customersEnabled = baseEnabled && (filter === 'all' || filter === 'customers')
  const ordersEnabled = baseEnabled && (filter === 'all' || filter === 'orders')

  const { data: rawProducts = [] } = useProductSearch(debouncedSearch, productsEnabled)
  const { data: rawCustomers = [] } = useCustomerSearch(debouncedSearch, customersEnabled)
  const { data: rawOrders = [] } = useSearchOrders(ordersEnabled ? debouncedSearch : '')

  // Only render results from the active filter. The query hooks gate
  // the *fetch* via the *Enabled flags above, but React Query keeps
  // results cached across filter changes — without this gate,
  // switching from "All" to "Customers" still rendered the products
  // group from the previous "All" search.
  const products = productsEnabled ? rawProducts : []
  const customers = customersEnabled ? rawCustomers : []
  const orders = ordersEnabled ? rawOrders : []

  // Reset search and filter when closing
  useEffect(() => {
    if (!open) {
      setSearch('')
      setFilter('all')
    }
  }, [open])

  // Keyboard shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        onOpenChange(!open)
      }
      if (e.key === 'Escape') {
        onOpenChange(false)
      }
    }
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [open, onOpenChange])

  const handleSelectProduct = (product: Product) => {
    // Open the ProductModal instead of dropping a single unit straight
    // into the cart — gives the cashier a chance to set qty / pick a
    // batch / apply a discount, matching what clicking a product card
    // on the grid does. The route owns the modal mount and listens
    // for this request via uiStore.
    requestProductModal(product)
    onOpenChange(false)
  }

  const handleSelectCustomer = (customer: Customer) => {
    // Nav + close go in onSuccess: a blocked customer change (e.g.
    // partial-payment lock) shouldn't dismiss the palette or switch
    // tabs as if it succeeded.
    selectCustomer.mutate(customer, {
      onSuccess: () => {
        setActiveTab('customers')
        onOpenChange(false)
      },
    })
  }

  const handleSelectOrder = (order: any) => {
    setOpenOrder({
      receiptId: order.id,
      number: order.number || String(order.id),
    })
    onOpenChange(false)
  }

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => onOpenChange(false)}
          />

          {/* Command Dialog */}
          <div className="absolute left-1/2 top-[20%] -translate-x-1/2 w-full max-w-xl">
        <Command
          className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
          shouldFilter={false}
        >
          {/* Search Input */}
          <div className="flex items-center px-4 border-b border-gray-100">
            <Search size={20} className="text-gray-400 shrink-0" />
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder="Search products, customers, or orders..."
              className="flex-1 px-3 py-4 text-base outline-none placeholder:text-gray-400"
              autoFocus
            />
            <button
              onClick={() => onOpenChange(false)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <X size={18} className="text-gray-400" />
            </button>
          </div>

          {/* Filter chips */}
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-gray-100">
            <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
              All
            </FilterChip>
            <FilterChip active={filter === 'products'} onClick={() => setFilter('products')}>
              <Package size={12} /> Products
            </FilterChip>
            <FilterChip active={filter === 'customers'} onClick={() => setFilter('customers')}>
              <User size={12} /> Customers
            </FilterChip>
            <FilterChip active={filter === 'orders'} onClick={() => setFilter('orders')}>
              <Receipt size={12} /> Orders
            </FilterChip>
          </div>

          {/* Results */}
          <Command.List className="max-h-[400px] overflow-y-auto p-2">
            {search.length < 2 ? (
              <div className="py-8 text-center text-gray-500 text-sm">
                Type at least 2 characters to search...
              </div>
            ) : products.length === 0 && customers.length === 0 && orders.length === 0 ? (
              <Command.Empty className="py-8 text-center text-gray-500">
                No results found.
              </Command.Empty>
            ) : (
              <>
                {/* Products */}
                {products.length > 0 && (
                  <Command.Group
                    heading={
                      <div className="flex items-center gap-2 px-2 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        <Package size={14} />
                        Products
                      </div>
                    }
                  >
                    {products.slice(0, 8).map((product) => {
                      const name = product.display_name || product.description || product.name || 'Unknown Product'
                      const brand = product.brand_name || product.vendor_name || product.brand || ''
                      const category = product.category || product.category_name || ''
                      const subtitle = [brand, category].filter(Boolean).join(' · ')
                      const image = product.image_url || product.thumbnail_url || product.image || null
                      const iconName = getCategoryIcon(category)
                      return (
                        <Command.Item
                          key={`product-${product.id}`}
                          value={`product-${product.id}`}
                          onSelect={() => handleSelectProduct(product)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer data-[selected=true]:bg-emerald-50 data-[selected=true]:text-emerald-900"
                        >
                          <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center text-green-600 overflow-hidden flex-shrink-0">
                            {image ? (
                              <img
                                src={withCdnImageWidth(image, 100)}
                                alt={name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none'
                                }}
                              />
                            ) : (
                              <ProductIcon name={iconName} size={20} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {name}
                            </div>
                            {subtitle && (
                              <div className="text-xs text-gray-500 truncate">
                                {subtitle}
                              </div>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-sm font-semibold text-gray-900">
                              ${formatPrice(product.price)}
                            </div>
                          </div>
                        </Command.Item>
                      )
                    })}
                  </Command.Group>
                )}

                {/* Customers */}
                {customers.length > 0 && (
                  <Command.Group
                    heading={
                      <div className="flex items-center gap-2 px-2 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mt-2">
                        <User size={14} />
                        Customers
                      </div>
                    }
                  >
                    {customers.slice(0, 5).map((customer) => {
                      const fullName =
                        customer.full_name
                        || customer.display_name
                        || [customer.first_name, customer.last_name].filter(Boolean).join(' ')
                        || customer.name
                        || `Customer #${customer.id}`
                      const initial = (customer.first_name || customer.last_name || customer.full_name || '?')[0]
                      return (
                        <Command.Item
                          key={`customer-${customer.id}`}
                          value={`customer-${customer.id}`}
                          onSelect={() => handleSelectCustomer(customer)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900"
                        >
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                            {initial}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {fullName}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {customer.phone || customer.email || 'No contact info'}
                            </div>
                          </div>
                          {customer.is_blocked && (
                            <span className="text-xs shrink-0"><BlockedBadge /></span>
                          )}
                        </Command.Item>
                      )
                    })}
                  </Command.Group>
                )}

                {/* Orders */}
                {orders.length > 0 && (
                  <Command.Group
                    heading={
                      <div className="flex items-center gap-2 px-2 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mt-2">
                        <Receipt size={14} />
                        Orders
                      </div>
                    }
                  >
                    {orders.slice(0, 8).map((order: any) => {
                      const isRefund =
                        order?.is_return === true
                        || Boolean(order?.src_id)
                        || Boolean(order?.return_receipt_id)
                      const total = Math.abs(Number(order?.totals?.total ?? 0))
                      const customerName = order?.customer?.display_name
                      const dateStr = order?.timestamp
                        ? new Date(order.timestamp).toLocaleDateString()
                        : ''
                      return (
                        <Command.Item
                          key={`order-${order.id}`}
                          value={`order-${order.id}`}
                          onSelect={() => handleSelectOrder(order)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer data-[selected=true]:bg-amber-50 data-[selected=true]:text-amber-900"
                        >
                          <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center text-amber-600">
                            <Receipt size={20} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">
                              #{order.number || order.id}
                              {isRefund && (
                                <span className="ml-2 text-xs font-semibold text-red-600">
                                  REFUND
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500 truncate">
                              {customerName ? `${customerName} · ${dateStr}` : dateStr || 'No date'}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className={`font-semibold tabular-nums ${isRefund ? 'text-red-600' : 'text-gray-900'}`}>
                              {isRefund ? '-' : ''}{formatCurrency(total)}
                            </div>
                          </div>
                        </Command.Item>
                      )
                    })}
                  </Command.Group>
                )}
              </>
            )}
          </Command.List>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 bg-gray-50 text-xs text-gray-500">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-medium">
                  ↑↓
                </kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-medium">
                  ↵
                </kbd>
                Select
              </span>
            </div>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-medium">
                Esc
              </kbd>
              Close
            </span>
          </div>
        </Command>
          </div>
        </div>
      )}
      {openOrder && (
        <OrderDetailModal
          receiptId={openOrder.receiptId}
          receiptNumber={openOrder.number}
          hideAddActions
          onClose={() => setOpenOrder(null)}
        />
      )}
    </>
  )
}

interface FilterChipProps {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}

function FilterChip({ active, onClick, children }: FilterChipProps) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer ${
        active
          ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
          : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
      }`}
    >
      {children}
    </button>
  )
}
